import { useSuspenseQuery } from '@apollo/client';
import React, { useState } from 'react';
import { useMatches } from 'react-router-dom';

import { useApiClient } from '@eluve/api-client-provider';
import { TranscriptText, TranscriptTimestampDivider } from '@eluve/blocks';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Card,
  EluveCardContent,
  EluveCardHeader,
  Icon,
  Stack,
  Switch,
  Text,
  useToast,
} from '@eluve/components';
import { useReviewHasuraContext } from '@eluve/session-helpers';
import { Translator } from '@eluve/translation';
import { formatTranscriptForDisplay } from '@eluve/utils';

import { SegmentAudioContextProvider } from './SegmentAudio';
import { getReviewerAppointmentDetails } from './operations';
import { useAppointmentParams } from './useAppointmentParams';

export const transcriptionSourceWithMeta = [
  'DEEPGRAM_NOVA_2_GENERAL',
  'DEEPGRAM_NOVA_2_MEDICAL',
  'DEEPGRAM_NOVA_3',
  'DEEPGRAM_NOVA_3_MEDICAL',
  'ELEVENLABS_SCRIBE_V2',
  'ELEVENLABS_SCRIBE_V2_TURBO',
];
export const AppointmentTranscripts: React.FC = () => {
  const { tenantId, appointmentId } = useAppointmentParams();
  const [showConfidenceStyles, setShowConfidenceStyles] = useState(true);
  const [translatedTranscripts, setTranslatedTranscripts] = useState<
    string[] | null
  >(null);
  const client = useApiClient();
  const [isLoadingUtteranceData, setIsLoadingUtteranceData] = useState(false);
  const [isClearingUtteranceData, setIsClearingUtteranceData] = useState(false);
  const [showClearConfirmation, setShowClearConfirmation] = useState(false);
  const toast = useToast();
  const context = useReviewHasuraContext();

  const { data, refetch } = useSuspenseQuery(getReviewerAppointmentDetails, {
    variables: {
      tenantId,
      appointmentId,
    },
    context,
  });

  const latestTranscriptionSources = data?.appointmentsByPk?.segments.map(
    (s) => {
      return {
        segmentId: s.id,
        transcriptionSource: s.raw_transcript
          .filter((rt) => rt.transcriptionStatus === 'COMPLETED')
          .sort(
            (a, b) =>
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
          )[0]?.transcriptionSource,
      };
    },
  );

  const isFromModelWithMeta = latestTranscriptionSources?.every((s) =>
    transcriptionSourceWithMeta.includes(s.transcriptionSource ?? ''),
  );

  const hasExistingUtterances = data?.appointmentsByPk?.segments.some(
    (s) => s.utterances.length > 0,
  );

  const hasUserEditedUtterances = data?.appointmentsByPk?.segments.some((s) =>
    s.utterances.some((u) => u.hasHumanUpdatedText),
  );

  const userEditedCount =
    data?.appointmentsByPk?.segments.reduce(
      (acc, s) =>
        acc + s.utterances.filter((u) => u.hasHumanUpdatedText).length,
      0,
    ) ?? 0;

  const canLoadEnrichedData =
    data?.appointmentsByPk?.segments.every((s) => s.utterances.length === 0) &&
    isFromModelWithMeta;

  const canClearUtterances = hasExistingUtterances && isFromModelWithMeta;

  const prepareUtteranceData = async () => {
    setIsLoadingUtteranceData(true);
    await client.reviewer.prepareUtterances({
      params: {
        tenantId,
        appointmentId,
      },
    });

    refetch();

    setIsLoadingUtteranceData(false);
  };

  const clearUtteranceData = async () => {
    setIsClearingUtteranceData(true);
    await client.reviewer.clearUtterances({
      params: {
        tenantId,
        appointmentId,
      },
    });

    refetch();

    setIsClearingUtteranceData(false);
    setShowClearConfirmation(false);
    toast.success('Utterances cleared');
  };

  const { appointmentsByPk: appointment } = data;
  const segments = appointment?.segments ?? [];

  const transcripts = segments.map((seg) => ({
    segmentId: seg.id,
    transcript: seg.transcript,
    recordingStartedAt: seg.recordingStartedAt,
    pausedAt: seg.recordingDuration
      ? new Date(
          new Date(seg.recordingStartedAt).getTime() + seg.recordingDuration,
        ).toISOString()
      : undefined,
  }));

  const fullTranscript = transcripts.map((t) => t.transcript).join('\n');

  const translationItems = transcripts.map((t) => t.transcript ?? '');

  const matches = useMatches();
  const isOnAdminPage = matches.some((m) => m.pathname.startsWith('/admin'));

  return (
    <div>
      <div className="w-1/6 p-2">
        <Text size="md">Need translation?</Text>
        <Translator
          items={translationItems}
          onTranslated={setTranslatedTranscripts}
        />
      </div>
      <Stack align="start" gap={2} direction="horizontal">
        <Card className="w-full">
          <EluveCardHeader>
            <Stack justify="between" gap={2} direction="horizontal">
              <Text className="whitespace-nowrap">
                Click on the text to jump to that portion of the audio
              </Text>
              <Stack fullWidth={false} gap={2} direction="horizontal">
                <Text className="whitespace-nowrap">
                  Show confidence styles
                </Text>
                <Switch
                  checked={showConfidenceStyles}
                  onCheckedChange={(checked) =>
                    setShowConfidenceStyles(checked)
                  }
                />
              </Stack>
            </Stack>
          </EluveCardHeader>
          <EluveCardContent>
            <Stack gap={5}>
              <Stack gap={2} direction="horizontal">
                {canLoadEnrichedData && (
                  <Button
                    size="sm"
                    disabled={isLoadingUtteranceData}
                    onClick={prepareUtteranceData}
                  >
                    <Icon name="CloudDownload" size="auto" />
                    Load enriched data
                  </Button>
                )}
                {canClearUtterances && (
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={isClearingUtteranceData}
                    onClick={() => setShowClearConfirmation(true)}
                  >
                    <Icon name="Trash" size="auto" />
                    Clear enriched data
                  </Button>
                )}
                <Button
                  size="sm"
                  onClick={async () => {
                    await navigator.clipboard.writeText(fullTranscript);
                    toast.success('Transcript copied to clipboard');
                  }}
                >
                  <Icon name="Copy" size="auto" />
                  Copy Transcript
                </Button>
                {!isFromModelWithMeta && (
                  <Text>
                    Current model does not support enriched data or transcript
                    editing
                  </Text>
                )}
              </Stack>
              {segments.map((s, i) => {
                return (
                  <SegmentAudioContextProvider
                    key={s.id}
                    index={i}
                    appointmentId={appointmentId}
                    seg={s}
                    segmentId={s.id}
                    tenantId={tenantId}
                    isReadonly={isOnAdminPage}
                  />
                );
              })}
            </Stack>
          </EluveCardContent>
        </Card>
        {translatedTranscripts && translatedTranscripts.length > 0 && (
          <Card className="w-full">
            <EluveCardHeader>
              <Stack justify="between" gap={2} direction="horizontal">
                <Text className="whitespace-nowrap">Translation</Text>
              </Stack>
            </EluveCardHeader>
            <EluveCardContent>
              <Stack gap={5}>
                {transcripts.map((t, i) => {
                  return (
                    <Stack key={t.segmentId} gap={2}>
                      <TranscriptTimestampDivider
                        timestamp={t.recordingStartedAt}
                        action={i === 0 ? 'started' : 'resumed'}
                      />
                      <TranscriptText>
                        {formatTranscriptForDisplay(
                          translatedTranscripts[i] ?? '',
                        )}
                      </TranscriptText>
                      {t.pausedAt && (
                        <TranscriptTimestampDivider
                          timestamp={t.pausedAt}
                          action="paused"
                        />
                      )}
                    </Stack>
                  );
                })}
              </Stack>
            </EluveCardContent>
          </Card>
        )}
      </Stack>

      <AlertDialog
        open={showClearConfirmation}
        onOpenChange={setShowClearConfirmation}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear enriched data?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete all utterances and their fragments for this
              appointment.
              {hasUserEditedUtterances && (
                <span className="mt-2 block font-semibold text-text-negative">
                  Warning: {userEditedCount} utterance
                  {userEditedCount !== 1 ? 's have' : ' has'} been manually
                  edited. These edits will be permanently lost.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={clearUtteranceData}
              disabled={isClearingUtteranceData}
            >
              {isClearingUtteranceData ? 'Clearing...' : 'Clear'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
